import { Legend } from '@/views/map/Legend/Legend';
import type { LayerConfigMap, RiskTypeConfig } from '@/views/map/layers/types';
import {
  DEFAULT_TREE_COVER_MIN_ZOOM,
  LayerType,
} from '@/views/map/layers/types';
import type { LegendItemProps } from '@/app/analysis';
import { analysisView } from '@/app/analysis';
import { GroupAnalysisType } from '@/generated/types/navigator/types.gen';
import { TREE_POINTS_ZOOM } from '@/views/map/layers/treesInDecline';
import { CRITICAL_TREES_ZOOM_LEVEL } from '@/views/map/layers/criticalTrees';
import { isTreeCoverActive } from '@/views/map/utils';
import { EncroachmentLegend } from './EncroachmentLegend';
import { TreeTypeLegend } from './TreeTypeLegend';
import { SegmentationLegend } from './SegmentationLegend';
import { SpeciesLegend } from './SpeciesLegend';
import { CriticalTreesLegend } from './CriticalTreesLegend';
import { FallInRiskLegend } from './FallInRiskLegend';
import { HeatmapLegend } from './HeatmapLegend';
import { WildfireLegend } from './WildfireLegend';
import { FuelHazardLegend } from './FuelHazardLegend';
import { WorktypeLegend } from './WorktypeLegend';
import { useHasFuelsData } from '@/hooks/useWildfireMode';

interface MapLegendProps {
  selectedTreeCover: string;
  showOverhang: boolean;
  zoom: number;
  layerConfig: LayerConfigMap;
  selectedAnalysis?: GroupAnalysisType;
}

export const MapLegend = (props: MapLegendProps) => {
  const {
    selectedTreeCover,
    showOverhang,
    zoom,
    layerConfig,
    selectedAnalysis = GroupAnalysisType.ENCROACHMENT,
  } = props;

  const hasFuelsData = useHasFuelsData();
  const isZoomedOut = zoom <= DEFAULT_TREE_COVER_MIN_ZOOM;

  const Analysis = analysisView[selectedAnalysis];
  const LegendItem: LegendItemProps | null =
    analysisView[selectedAnalysis].getLegendItemPerAnalysis();

  const treeLayerConfig = layerConfig[LayerType.trees] as RiskTypeConfig;

  return (
    <Legend data-testid="map-legend">
      {zoom <= TREE_POINTS_ZOOM &&
        treeLayerConfig.visible &&
        treeLayerConfig.showHeatmap && <HeatmapLegend />}
      {Analysis.mapLayers.includes(LayerType.trees) &&
        !treeLayerConfig.isHazardTreesOnly &&
        zoom > TREE_POINTS_ZOOM && (
          <FallInRiskLegend layerConfig={treeLayerConfig} />
        )}
      {analysisView !== undefined && LegendItem.Component !== undefined && (
        <LegendItem.Component
          selectedAnalysis={selectedAnalysis}
          isZoomedOut={isZoomedOut}
        />
      )}
      {layerConfig[LayerType.critical_trees].visible &&
        zoom > CRITICAL_TREES_ZOOM_LEVEL && <CriticalTreesLegend />}
      {isTreeCoverActive(layerConfig) && !isZoomedOut && (
        <>
          {selectedTreeCover === LayerType[LayerType.segmentation] && (
            <SegmentationLegend />
          )}
          {selectedTreeCover === LayerType[LayerType.encroachment] && (
            <EncroachmentLegend showOverhang={showOverhang} />
          )}
          {selectedTreeCover === LayerType[LayerType.tree_species] && (
            <SpeciesLegend />
          )}
          {selectedTreeCover === LayerType[LayerType.tree_type] && (
            <TreeTypeLegend />
          )}
        </>
      )}
      {layerConfig[LayerType.wildfire_risk].visible && <WildfireLegend />}
      {layerConfig[LayerType.fuel_hazard].visible && hasFuelsData && (
        <FuelHazardLegend />
      )}
      {layerConfig[LayerType.worktype_analysis].visible && <WorktypeLegend />}
    </Legend>
  );
};
